import React, { useState, useEffect, useRef } from 'react';
import {
  User,
  Mail,
  Phone,
  Building2,
  Camera,
  Save,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Loader2,
  Shield,
  Calendar,
  BadgeCheck,
  X,
  Trash2,
} from 'lucide-react';
import { useAuth, ROLES, ACCOUNT_TYPES } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { useActivityLog, LOG_TYPES, LOG_CATEGORIES } from '../context/ActivityLogContext';

const Profile = () => {
  const { user, updateProfile, changePassword, isLoading } = useAuth();
  const { t } = useTranslation();
  const { addLog } = useActivityLog();
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const [stats, setStats] = useState({ loansCount: 0, complianceRate: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    position: user?.position || '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });


  useEffect(() => {
    const loadStats = async () => {
      setStatsLoading(true);
      try {

        const loansResult = await window.electronAPI?.loan?.getStats();

        const complianceResult = await window.electronAPI?.analytics?.getCurrentCompliance();

        const loansCount = loansResult?.success ? loansResult.data?.totalLoans || 0 : 0;
        const complianceRate = complianceResult?.success ? complianceResult.data?.complianceRate || 0 : 0;

        setStats({
          loansCount,
          complianceRate: Math.round(complianceRate)
        });
      } catch (error) {
        console.error('Error loading profile stats:', error);
      }
      setStatsLoading(false);
    };

    loadStats();
  }, []);


  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: t('profile.avatarSizeError') || 'Image must not exceed 2 MB' });
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setAvatarPreview(event.target.result);
        setShowAvatarModal(true);
      };
      reader.readAsDataURL(file);
    }
  };


  const handleSaveAvatar = async () => {
    if (!avatarPreview) return;
    setIsUploadingAvatar(true);

    try {

      const result = await updateProfile({ ...profileData, avatar: avatarPreview });

      if (result.success) {
        setMessage({ type: 'success', text: t('profile.avatarUpdateSuccess') || 'Photo updated successfully' });
        setShowAvatarModal(false);
        setAvatarPreview(null);
        addLog({
          type: LOG_TYPES.ACTION,
          category: LOG_CATEGORIES.USER,
          action: 'update_avatar',
          message: 'Photo de profil mise à jour',
          details: {}
        });
      } else {
        setMessage({ type: 'error', text: result.error || t('profile.avatarUpdateError') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }

    setIsUploadingAvatar(false);
  };


  const handleRemoveAvatar = async () => {
    setIsUploadingAvatar(true);

    try {
      const result = await updateProfile({ ...profileData, avatar: null });

      if (result.success) {
        setMessage({ type: 'success', text: t('profile.avatarRemoved') || 'Photo removed' });
        setShowAvatarModal(false);
        addLog({
          type: LOG_TYPES.ACTION,
          category: LOG_CATEGORIES.USER,
          action: 'remove_avatar',
          message: 'Photo de profil supprimée',
          details: {}
        });
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }

    setIsUploadingAvatar(false);
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    const result = await updateProfile(profileData);
    if (result.success) {
      setMessage({ type: 'success', text: t('profile.updateSuccess') });
      setIsEditing(false);
      addLog({
        type: LOG_TYPES.ACTION,
        category: LOG_CATEGORIES.USER,
        action: 'update_profile',
        message: `Profil mis à jour: ${profileData.firstName} ${profileData.lastName}`,
        details: {
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          email: profileData.email
        }
      });
    } else {
      setMessage({ type: 'error', text: result.error || t('profile.updateError') });
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: t('profile.passwordTooShortError') });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: t('profile.passwordMismatchError') });
      return;
    }

    const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
    if (result.success) {
      setMessage({ type: 'success', text: t('profile.passwordChangeSuccess') });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      addLog({
        type: LOG_TYPES.ACTION,
        category: LOG_CATEGORIES.USER,
        action: 'change_password',
        message: 'Mot de passe modifié avec succès',
        details: {}
      });
    } else {
      setMessage({ type: 'error', text: result.error || t('profile.passwordChangeError') });
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      [ROLES.AGENT]: { label: t('roles.agent') || 'Facility Agent', color: 'bg-purple-100 text-purple-700' },
      [ROLES.LENDER]: { label: t('roles.lender') || 'Lender', color: 'bg-blue-100 text-blue-700' },
      [ROLES.BORROWER]: { label: t('roles.borrower') || 'Borrower', color: 'bg-green-100 text-green-700' },
    };
    return badges[role] || { label: role, color: 'bg-slate-100 text-slate-700' };
  };

  const getInitials = () => {
    return `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const tabs = [
    { id: 'profile', label: t('profile.informations'), icon: User },
    { id: 'security', label: t('settings.security'), icon: Lock },
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 mb-6 text-white">
        <div className="flex flex-col md:flex-row items-center gap-6">
          {}
          <div className="relative">
            <div className="w-24 h-24 bg-white/20 rounded-2xl flex items-center justify-center text-3xl font-bold">
              {user?.avatar ? (
                <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover rounded-2xl" />
              ) : (
                getInitials()
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAvatarChange}
              accept="image/*"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute -bottom-2 -right-2 w-8 h-8 bg-white text-blue-600 rounded-lg flex items-center justify-center shadow-lg hover:bg-slate-50 transition-colors"
            >
              <Camera className="w-4 h-4" />
            </button>
          </div>

          {}
          <div className="text-center md:text-left flex-1">
            <h1 className="text-2xl font-bold mb-1">
              {user?.firstName} {user?.lastName}
            </h1>
            <p className="text-blue-100 mb-3">{user?.email}</p>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadge(user?.role).color}`}>
                {getRoleBadge(user?.role).label}
              </span>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm flex items-center gap-1">
                {user?.accountType === ACCOUNT_TYPES.ENTERPRISE ? (
                  <>
                    <Building2 className="w-4 h-4" />
                    {t('auth.enterprise')}
                  </>
                ) : (
                  <>
                    <User className="w-4 h-4" />
                    {t('auth.individual')}
                  </>
                )}
              </span>
            </div>
          </div>

          {}
          <div className="flex gap-6 text-center">
            <div>
              <p className="text-2xl font-bold">
                {statsLoading ? <Loader2 className="w-6 h-6 animate-spin inline" /> : stats.loansCount}
              </p>
              <p className="text-blue-100 text-sm">{t('profile.loansManaged')}</p>
            </div>
            <div>
              <p className="text-2xl font-bold">
                {statsLoading ? <Loader2 className="w-6 h-6 animate-spin inline" /> : `${stats.complianceRate}%`}
              </p>
              <p className="text-blue-100 text-sm">{t('profile.compliance')}</p>
            </div>
          </div>
        </div>
      </div>

      {}
      {message.text && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      {}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setMessage({ type: '', text: '' });
                }}
                className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileSubmit}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">{t('profile.personalInfo')}</h2>
                  <p className="text-slate-500 text-sm">{t('profile.subtitle')}</p>
                </div>
                {!isEditing ? (
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-colors"
                  >
                    {t('common.edit')}
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        setProfileData({
                          firstName: user?.firstName || '',
                          lastName: user?.lastName || '',
                          email: user?.email || '',
                          phone: user?.phone || '',
                          company: user?.company || '',
                          position: user?.position || '',
                        });
                      }}
                      className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {t('common.save')}
                    </button>
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('profile.firstName')}</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleProfileChange}
                      disabled={!isEditing}
                      className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('profile.lastName')}</label>
                  <input
                    type="text"
                    name="lastName"
                    value={profileData.lastName}
                    onChange={handleProfileChange}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('profile.email')}
                    <span className="ml-2 text-xs text-slate-400 font-normal">({t('profile.emailNotEditable') || 'Non modifiable'})</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      name="email"
                      value={profileData.email}
                      disabled={true}
                      className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('profile.phone')}</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleProfileChange}
                      disabled={!isEditing}
                      className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-500 transition-colors"
                    />
                  </div>
                </div>

                {user?.accountType === ACCOUNT_TYPES.ENTERPRISE && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">{t('profile.company')}</label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          name="company"
                          value={profileData.company}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                          className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-500 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">{t('profile.position')}</label>
                      <input
                        type="text"
                        name="position"
                        value={profileData.position}
                        onChange={handleProfileChange}
                        disabled={!isEditing}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-500 transition-colors"
                      />
                    </div>
                  </>
                )}
              </div>

              {}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h3 className="text-sm font-semibold text-slate-800 mb-4">{t('profile.accountInfo')}</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <Calendar className="w-4 h-4" />
                      {t('profile.createdDate')}
                    </div>
                    <p className="font-medium text-slate-800">{user?.createdAt || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <Shield className="w-4 h-4" />
                      {t('profile.role')}
                    </div>
                    <p className="font-medium text-slate-800">{getRoleBadge(user?.role).label}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <BadgeCheck className="w-4 h-4" />
                      {t('profile.status')}
                    </div>
                    <p className="font-medium text-green-600">{t('profile.verifiedAccount')}</p>
                  </div>
                </div>
              </div>
            </form>
          )}

          {}
          {activeTab === 'security' && (
            <div>
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-slate-800">{t('settings.changePassword')}</h2>
                <p className="text-slate-500 text-sm">
                  {t('profile.passwordHint')}
                </p>
              </div>

              <form onSubmit={handlePasswordSubmit} className="max-w-md space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('settings.currentPassword')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      className="w-full pl-11 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('settings.newPassword')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full pl-11 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('settings.confirmNewPassword')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('profile.changingPassword')}
                    </>
                  ) : (
                    t('settings.changePassword')
                  )}
                </button>
              </form>

              {}
              <div className="mt-10 pt-6 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">{t('settings.sessions')}</h3>
                <div className="space-y-3">
                  <div className="p-4 bg-slate-50 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <Shield className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{t('profile.currentSession')}</p>
                        <p className="text-slate-500 text-sm">macOS · Chrome · Paris, France</p>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                      {t('profile.activeNow')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {}
      {showAvatarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">{t('profile.changePhoto') || 'Changer la photo'}</h2>
                <button
                  onClick={() => {
                    setShowAvatarModal(false);
                    setAvatarPreview(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {}
              <div className="flex justify-center mb-6">
                <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-blue-100">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : user?.avatar ? (
                    <img src={user.avatar} alt="Current" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-3xl font-bold">
                      {getInitials()}
                    </div>
                  )}
                </div>
              </div>

              <p className="text-sm text-slate-500 text-center mb-6">
                {t('profile.avatarRequirements') || 'PNG, JPG ou GIF. Max 2 Mo.'}
              </p>

              {}
              <div className="flex gap-3">
                {user?.avatar && !avatarPreview && (
                  <button
                    type="button"
                    onClick={handleRemoveAvatar}
                    disabled={isUploadingAvatar}
                    className="flex-1 py-3 px-4 border border-red-300 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-5 h-5" />
                    {t('profile.removePhoto') || 'Supprimer'}
                  </button>
                )}
                {avatarPreview && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setAvatarPreview(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="flex-1 py-3 px-4 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveAvatar}
                      disabled={isUploadingAvatar}
                      className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {isUploadingAvatar ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {t('common.loading')}
                        </>
                      ) : (
                        t('common.save')
                      )}
                    </button>
                  </>
                )}
                {!avatarPreview && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Camera className="w-5 h-5" />
                    {t('profile.selectPhoto') || 'Sélectionner une photo'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;